/**
 * CycleJournalCapture — lightweight journal prompt shown after the Reflect step
 * in Review & Reset. Calm, optional, never blocks progress.
 */
import { useState } from "react";
import { usePrivacy } from "./PrivacyProvider";
import { BookOpen } from "lucide-react";

export function CycleJournalCapture({ sessionId }) {
  const { saveJournal, isSolo, hasPersonal, isSeparate } = usePrivacy();
  const showToggle = !isSolo && (hasPersonal || isSeparate);
  const [text, setText] = useState("");
  const [visibility, setVisibility] = useState(isSolo ? "private" : "shared");
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  if (saved) {
    return (
      <div className="bg-[#1a4d3e]/5 border border-[#1a4d3e]/10 rounded-xl p-4 text-center" data-testid="cycle-journal-saved">
        <p className="text-sm text-[#1a4d3e] font-medium">Noted. Nice one.</p>
      </div>
    );
  }

  const handleSave = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await saveJournal(
        text.trim(),
        "review_reset",
        isSolo ? "private" : visibility,
        sessionId || null,
        "cycle",
        sessionId || null
      );
      setSaved(true);
    } catch {
      /* silent */
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-4 space-y-3" data-testid="cycle-journal-capture">
      <div className="flex items-center gap-2">
        <BookOpen className="w-4 h-4 text-[#1a4d3e]" />
        <h4 className="text-sm font-medium">Capture this cycle</h4>
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed">
        Write a quick note about how this cycle felt, or anything you want to remember.
      </p>
      <input
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="This cycle felt..."
        className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background focus:outline-none focus:ring-1 focus:ring-[#1a4d3e]/30"
        data-testid="cycle-journal-input"
      />
      {showToggle && (
        <div className="flex gap-2" data-testid="cycle-journal-visibility-toggle">
          <button
            type="button"
            onClick={() => setVisibility("shared")}
            className={`text-xs px-3 py-1 rounded-full border transition-colors ${
              visibility === "shared"
                ? "bg-[#1a4d3e]/10 border-[#1a4d3e]/30 text-[#1a4d3e]"
                : "border-border text-muted-foreground hover:border-slate-300"
            }`}
            data-testid="cycle-journal-vis-shared"
          >
            Shared note
          </button>
          <button
            type="button"
            onClick={() => setVisibility("private")}
            className={`text-xs px-3 py-1 rounded-full border transition-colors ${
              visibility === "private"
                ? "bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-foreground"
                : "border-border text-muted-foreground hover:border-slate-300"
            }`}
            data-testid="cycle-journal-vis-private"
          >
            Private note
          </button>
        </div>
      )}
      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={() => setSaved(true)}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1"
          data-testid="cycle-journal-skip"
        >
          Skip
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={!text.trim() || saving}
          className="text-xs font-medium text-[#1a4d3e] hover:text-[#1a4d3e]/80 disabled:opacity-40 disabled:cursor-not-allowed px-3 py-1 bg-[#1a4d3e]/10 rounded-full transition-colors"
          data-testid="cycle-journal-save"
        >
          {saving ? "Saving..." : "Save note"}
        </button>
      </div>
    </div>
  );
}
